import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {
    AddressResult,
    ApplicationFacade,
    Departure,
    FavoriteLocation,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    Stop,
    StopRouteResult,
    TransitNetworkPartType,
    TripClickEvent
} from '@ridango/components';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import {
    ExploreSearchModalComponent,
    ExploreSearchModalResultType
} from '../explore-search-modal/explore-search-modal.component';
import { FavoritesListModalComponent } from '../favorites-list-modal/favorites-list-modal.component';
import Env from '@ridango/core/env';
import { catchError, filter, map, take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, forkJoin, interval, of, timer } from 'rxjs';
import { FavoritesFacade } from '@ridango/components/lib/favorites';
import { AppStartupService, NetworkService } from '@ridango/core/lib/services';
import { LatLng as RidLatLng, BaseComponent, TimeUtil } from '@ridango/core';
import { getDistance } from 'geolib';
import { FavoriteSchedule } from '@ridango/components/lib/favorites/favorites.model';
import { AlertsService } from '@ridango/core/lib/services/alerts.service';
import { SmallScreenSheetModalContainerService } from '../../small-screen-sheet-modal-container/small-screen-sheet-modal-container.service';
import { Capacitor } from '@capacitor/core';
import { LatLng } from 'leaflet';
import { Debounce } from '../../../decorators/debounce.decorator';
import { MapApiService } from '../../map/services';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { RidangoScrollIntoViewDirective } from '@ridango/components/lib/core-ui/directives/scroll-into-view.directive';
import { Temporal } from '@js-temporal/polyfill';

@Component({
    selector: 'rwidget-explore-search',
    templateUrl: './explore-search.component.html',
    styleUrls: ['./explore-search.component.scss']
})
export class ExploreSearchComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('stickyHeader') stickyHeader: TemplateRef<unknown>;
    @ViewChildren('stopElement') stopElements!: QueryList<RidangoScrollIntoViewDirective>;
    @ViewChildren('favoriteStopElement') favoriteStopElements!: QueryList<RidangoScrollIntoViewDirective>;
    readonly DEPARTURES_SEARCH_RANGE_SECONDS = 60 * 60 * 24;
    readonly NEARBY_RADIUS_METERS = 2000;
    readonly TABS = {
        nearby: 'nearby',
        favorites: 'favorites',
        routes: 'routes'
    };
    private timezone: string = this.appStartupService.getTimezone();

    hasLocationError = false;
    selectedTab = this.TABS.nearby;
    favoriteStopsSaved = [];
    favoriteStopsShown = [];
    coordinates: LatLng;
    isSearchingNearbyStops = true;
    departuresLoadingMap: { [stopId: string]: boolean } = {};

    assetsPath$ = this.applicationFacade.assetsPath$;
    showNumberOfStops = 3;
    showNumberOfFavorites = 3;
    STOPS_INCREMENT = 3;
    FAVORITES_INCREMENT = this.env.EXPLORE?.LOAD_MORE_FAVORITE_STOPS_BY ?? 3;
    nearbyStops: Stop[] = [];
    stopIdToDepartures: Map<string, Departure[]> = new Map<string, Departure[]>();
    favoriteLocations$ = this.favoritesFacade.favoriteLocations$;
    favoriteSchedules$ = this.favoritesFacade.favoriteSchedules$;
    routes$ = this.openTripPlannerFacade.routes$;
    stops$ = this.openTripPlannerFacade.stops$;
    favoriteStops$ = this.favoritesFacade.favoriteStops$;
    routesGroups$ = this.openTripPlannerFacade.routesGroups$;
    imageMapLocationPath = this.env.EXPLORE?.IMAGE_MAP_LOCATION_PATH || '/img/explore/location.svg';
    favoriteLocations = [];
    lastCheckedLocation: RidLatLng = null;
    favoriteSchedules: FavoriteSchedule[] = [];
    deviceStatus = this.network.getCurrentStatus();
    deviceStatus$ = this.network.getStatus();
    componentInited = false;
    private readonly SEARCH_FOR_STOP = 'Search for stop';
    private readonly SHOW_MORE_STOPS = 'Show more stops';
    private readonly SHOW_ALL = 'Show all';
    initialWindowWidth: number = window.innerWidth;
    isMobileView: boolean = window.innerWidth < 768;
    private lastSearchbarPointerDownAt = 0;
    private suppressAutoFocusUntil = 0;
    private readonly SEARCHBAR_POINTER_GRACE_MS = 350;
    private readonly SEARCHBAR_INIT_SUPPRESS_MS = 700;
    private lastRefreshTimeSubject$ = new BehaviorSubject<Temporal.Instant | null>(null);
    lastRefreshTime$ = this.lastRefreshTimeSubject$.asObservable();
    private currentTimeSubject$ = new BehaviorSubject<Temporal.Instant>(Temporal.Now.instant());

    minutesSinceRefresh$ = combineLatest([this.lastRefreshTimeSubject$, this.currentTimeSubject$]).pipe(
        map(([lastRefresh, currentTime]) => {
            if (!lastRefresh) {
                return 0;
            }
            const duration = lastRefresh.until(currentTime);
            return Math.floor(duration.total({ unit: 'minute' }));
        })
    );

    @HostListener('window:resize')
    onResize() {
        if (this.initialWindowWidth !== window.innerWidth) {
            this.initialWindowWidth = window.innerWidth;
            this.isMobileView = window.innerWidth < 768;
        }
    }

    constructor(
        private applicationFacade: ApplicationFacade,
        private otpService: OpenTripPlannerService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private favoritesFacade: FavoritesFacade,
        private router: Router,
        private mapApi: MapApiService,
        private alertsService: AlertsService,
        private route: ActivatedRoute,
        private modalCtrl: ModalController,
        private smallScreenSheetModalContainerService: SmallScreenSheetModalContainerService,
        @Inject('ENV') public env: Env,
        private cdr: ChangeDetectorRef,
        private network: NetworkService,
        private analyticsService: AnalyticsService,
        public appStartupService: AppStartupService
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.env.OFFLINE_MODE?.CACHE_FAVORITE_SCHEDULES) {
            this.deviceStatus$.pipe(takeUntil(this.componentDestroyed$)).subscribe((status) => {
                this.deviceStatus = status;

                if (this.deviceStatus && !this.componentInited) {
                    this.init();
                }
            });
        }

        if (this.deviceStatus || !this.env.OFFLINE_MODE?.CACHE_FAVORITE_SCHEDULES) {
            this.init();
        } else {
            this.isSearchingNearbyStops = false;
            this.selectedTab = this.TABS.favorites;
        }

        if (this.env.EXPLORE?.SHOW_FAVORITE_SCHEDULE) {
            combineLatest([this.routes$, this.stops$])
                .pipe(
                    filter(([routes, stops]) => {
                        return !!routes.length && !!stops.length;
                    }),
                    take(1),
                    takeUntil(this.componentDestroyed$)
                )
                .subscribe(() => this.favoritesFacade.getFavoriteSchedules());

            this.favoriteSchedules$.pipe(takeUntil(this.componentDestroyed$)).subscribe((favoriteSchedules) => {
                this.favoriteSchedules = favoriteSchedules;
            });
        }

        if (this.env.EXPLORE?.SHOW_ROUTES || this.env.EXPLORE?.SHOW_FAVORITE_SCHEDULE) {
            this.openTripPlannerFacade.getAllRoutes();
            this.openTripPlannerFacade.getAllStops();
            this.routeQueryParamsSubscription();
        }

        interval(60000)
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter(() => this.selectedTab === this.TABS.nearby || this.selectedTab === this.TABS.favorites)
            )
            .subscribe(() => {
                this.currentTimeSubject$.next(Temporal.Now.instant());
            });
    }

    init(): void {
        const now = Temporal.Now.instant();
        this.lastRefreshTimeSubject$.next(now);
        this.currentTimeSubject$.next(now);
        this.setNearbyStops();
        this.updateStopTimesOfNewVisibleStops();
        this.syncFavorites();

        if (this.env.EXPLORE?.SHOW_ROUTE_GROUPS) {
            this.openTripPlannerFacade.getRoutesGroups();
        }

        this.favoriteLocations$.pipe(takeUntil(this.componentDestroyed$)).subscribe((locations) => {
            this.favoriteLocations = locations;
        });

        if (this.env.EXPLORE?.SHOW_FAVORITE_STOPS) {
            combineLatest(
                this.favoritesFacade.favoriteStops$,
                this.openTripPlannerFacade.stops$,
                this.openTripPlannerFacade.location$
            )
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(([favorites, stops, location]) => {
                    if (!stops?.length || !favorites?.length) {
                        this.favoriteStopsSaved = [];
                        this.favoriteStopsShown = [];

                        return;
                    }
                    const updatedStops = stops
                        .filter((stop) => !!favorites.find((favorite) => favorite.stopId === stop.id))
                        .map((stop) => {
                            const fav = favorites.find((f) => f.stopId === stop.id);

                            return { ...stop, stopId: fav.stopId, name: fav.alias || fav.name || stop.name };
                        });

                    if (location?.coordinates) {
                        if (this.lastCheckedLocation) {
                            const coveredDistance = this.calculateDistanceToPreviousPoint(location.coordinates);

                            // get the nearest stops only if the distance to the last point is at least 10 meters
                            if (coveredDistance <= 10 && this.favoriteStopsSaved?.length === updatedStops?.length) {
                                return;
                            }
                        }

                        this.lastCheckedLocation = location.coordinates;

                        this.favoriteStopsSaved = updatedStops.map((favorite: Stop) => ({
                            ...favorite,
                            dist: getDistance(
                                {
                                    latitude: favorite.lat,
                                    longitude: favorite.lon
                                },
                                {
                                    latitude: location.coordinates?.lat,
                                    longitude: location.coordinates?.lng
                                }
                            )
                        }));
                        this.favoriteStopsShown = [...this.favoriteStopsSaved.slice(0, this.showNumberOfFavorites)];
                        this.favoriteStopsShown.forEach((stop) => {
                            this.departuresLoadingMap[stop.id] = true;
                        });

                        if (this.env.OTP_BASE_URL) {
                            const observables = this.favoriteStopsSaved.map((stop) => {
                                return this.otpService.getStopDescription(stop.id).pipe(
                                    map((description) => ({ ...stop, desc: description })),
                                    catchError(() => of(stop))
                                );
                            });

                            forkJoin(observables)
                                .pipe(takeUntil(this.componentDestroyed$))
                                .subscribe((stopsWithDescriptions) => {
                                    this.favoriteStopsSaved = stopsWithDescriptions;
                                    this.favoriteStopsShown = [
                                        ...this.favoriteStopsSaved.slice(0, this.showNumberOfFavorites)
                                    ];
                                    this.getFavoriteAlerts();
                                    this.updateStopTimesOfNewVisibleStops();
                                });
                        } else {
                            this.getFavoriteAlerts();
                            this.updateStopTimesOfNewVisibleStops();
                        }
                    } else {
                        this.favoriteStopsSaved = updatedStops;
                        this.favoriteStopsShown = [...this.favoriteStopsSaved.slice(0, this.showNumberOfFavorites)];
                        this.getFavoriteAlerts();
                        this.updateStopTimesOfNewVisibleStops();
                    }
                });
        }

        // We shouldn’t trigger refresh too often.
        // Currently, departure times update at set intervals after departure: starting at 17:03, then "5 min left", "1 min left", "now" label,
        // followed by incorrect time that signals a trip refresh.
        if (this.env.SECONDS_TO_REFRESH_DEPARTURE_TIMES) {
            timer(0, this.env.SECONDS_TO_REFRESH_DEPARTURE_TIMES * TimeUtil.MILLISECONDS_IN_SECOND)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(() => {
                    this.refreshStopTimes();
                });
        }

        this.openTripPlannerFacade.refreshStopTimes$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((stopIds: string[]) => {
                this.refreshStopTimes(stopIds);
            });
    }

    ngAfterViewInit(): void {
        // Prevent searchbar auto-focus from immediately opening the search modal
        // right after view initialization. Ionic may trigger focus programmatically
        // during render/layout, so we temporarily suppress focus handling and allow
        // only real user interactions (pointer/keyboard) after this grace period.
        this.suppressAutoFocusUntil = Date.now() + this.SEARCHBAR_INIT_SUPPRESS_MS;
        this.smallScreenSheetModalContainerService.triggerCalculateBreakpoint();
        if (this.stickyHeader) {
            this.smallScreenSheetModalContainerService.setHeaderTemplate(this.stickyHeader);
        }
    }

    getFavoriteAlerts() {
        if (this.env.EXPLORE?.ALERTS_ON_STOPS) {
            const observables = this.favoriteStopsSaved.map((stop) => {
                return this.alertsService.getStopAlerts(stop.id).pipe(
                    map((response) => ({ ...stop, disruptionMessages: response?.messages })),
                    catchError(() => of(stop))
                );
            });

            if (!observables.length) {
                return;
            }

            forkJoin(observables).subscribe((response) => {
                this.favoriteStopsSaved = response;
                this.favoriteStopsShown = [...this.favoriteStopsSaved.slice(0, this.showNumberOfFavorites)];
            });
        }
    }

    setNearbyStops() {
        this.nearbyStops = [];
        this.openTripPlannerFacade.location$
            .pipe(filter(Boolean), take(1))
            .subscribe((location: { coordinates?: { lat: number; lng: number } }) => {
                this.openTripPlannerFacade.fetchNearbyStops(
                    location.coordinates?.lat,
                    location.coordinates?.lng,
                    this.NEARBY_RADIUS_METERS
                );
            });

        this.openTripPlannerFacade.location$.pipe(takeUntil(this.componentDestroyed$)).subscribe((location) => {
            this.hasLocationError = location.hasError;

            if (this.hasLocationError) {
                this.openTripPlannerFacade.setCurrentCoordinates(null);
            }

            if (Capacitor.isNativePlatform() && location.coordinates) {
                this.coordinates = new LatLng(location.coordinates?.lat, location.coordinates?.lng);
            }

            if (this.env.EXPLORE?.SHOW_ROUTES && this.hasLocationError) {
                if (this.env.IS_MOBILE_APP) {
                    this.selectedTab = this.TABS.routes;
                } else {
                    this.router.navigate([], { queryParams: { selectedTab: this.TABS.routes } });
                }
            } else {
                this.selectedTab = !this.hasLocationError ? this.TABS.nearby : this.TABS.favorites;
            }
        });

        this.setStopsInRadius();
    }

    setStopsInRadius(): void {
        this.isSearchingNearbyStops = true;
        this.openTripPlannerFacade.nearbyStopsWithAlerts$.pipe(takeUntil(this.componentDestroyed$)).subscribe(
            (response) => {
                if (!response) {
                    return;
                }

                response.forEach((stop) => {
                    this.departuresLoadingMap[stop.id] = true;
                });
                this.nearbyStops = response;
                this.updateMapStopsState();
                this.updateStopTimesOfNewVisibleStops();
                this.isSearchingNearbyStops = false;
            },
            (error) => {
                this.nearbyStops = [];
                this.isSearchingNearbyStops = false;
                this.cdr.detectChanges();
                console.error('Error while fetching nearby stops', error);
            },
            () => {
                this.isSearchingNearbyStops = false;
                this.cdr.detectChanges();
            }
        );
    }

    getNearbyStopsPatch(): Stop[] {
        return this.nearbyStops.slice(0, this.showNumberOfStops);
    }

    markSearchbarPointerDown(): void {
        this.lastSearchbarPointerDownAt = Date.now();
    }

    /**
     * Determines whether searchbar focus should trigger opening the search modal.
     *
     * Guards against unintended auto-focus scenarios that can occur:
     * - right after component initialization
     * - when focus is triggered programmatically
     *
     * @param event Optional focus event passed from the template.
     * @returns `true` if the search modal should be opened, otherwise `false`.
     */
    private shouldHandleSearchbarFocus(event?: Event): boolean {
        const now = Date.now();

        const isKeyboardEvent = typeof KeyboardEvent !== 'undefined' && event instanceof KeyboardEvent;

        const isPointerInteractionRecent =
            this.lastSearchbarPointerDownAt > 0 &&
            now - this.lastSearchbarPointerDownAt < this.SEARCHBAR_POINTER_GRACE_MS;

        const isWithinAutoFocusSuppressionWindow = now < this.suppressAutoFocusUntil;

        if (isWithinAutoFocusSuppressionWindow && !isPointerInteractionRecent && !isKeyboardEvent) {
            return false;
        }

        return true;
    }

    async openSearchModal(event?: Event): Promise<void> {
        if (!this.shouldHandleSearchbarFocus(event)) {
            return;
        }

        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.EXPLORE,
            name: this.SEARCH_FOR_STOP
        });

        const modal = await this.modalCtrl.create({
            component: ExploreSearchModalComponent
        });
        await modal.present();

        const { data, role } = await modal.onWillDismiss();

        if (data && role === ExploreSearchModalResultType.Confirm) {
            this.analyticsService.eventTrack(TrackedEventAction.SEARCHED, {
                category: TrackedEventCategory.EXPLORE,
                name: `${data?.name}`
            });
            this.handleSearchItemSelect(data);
        } else {
            this.analyticsService.eventTrack(TrackedEventAction.SEARCH_CANCELED, {
                category: TrackedEventCategory.EXPLORE,
                label: 'Search Canceled'
            });
        }
    }

    loadMoreStops(): void {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.EXPLORE,
            name: this.SHOW_MORE_STOPS
        });

        const previousCount = this.showNumberOfStops;
        this.showNumberOfStops += this.STOPS_INCREMENT;
        this.updateMapStopsState();
        this.updateStopTimesOfNewVisibleStops();
        // wait for the new stops to render
        this.cdr.detectChanges();
        const firstNewStopElement = this.stopElements.toArray()[previousCount];
        firstNewStopElement?.scrollTo();
    }

    loadMoreFavorites(): void {
        this.showNumberOfFavorites += this.FAVORITES_INCREMENT;
        // mark newly visible favorites as loading (same as loadMoreStops)
        const previousCount = this.showNumberOfFavorites - this.FAVORITES_INCREMENT;
        this.favoriteStopsShown = [...this.favoriteStopsSaved.slice(0, this.showNumberOfFavorites)];
        this.favoriteStopsShown.forEach((stop) => {
            this.departuresLoadingMap[stop.id] = true;
        });

        this.updateStopTimesOfNewVisibleStops();
        // scroll to the first newly visible favorite stop
        this.cdr.detectChanges();
        const firstNewFavoriteElement = this.favoriteStopElements.toArray()[previousCount];
        firstNewFavoriteElement?.scrollTo();
    }

    routeQueryParamsSubscription(): void {
        this.route.queryParams.pipe(takeUntil(this.componentDestroyed$)).subscribe(({ selectedTab }) => {
            if (selectedTab) {
                this.selectedTab = selectedTab;
            }
        });
    }

    showLoadMoreStops(): boolean {
        return this.nearbyStops.length > this.showNumberOfStops;
    }

    hasNoNearbyStops(): boolean {
        return !this.isSearchingNearbyStops && this.nearbyStops?.length === 0;
    }

    openStopView(stopId: string, disableAnalytics = false) {
        !disableAnalytics &&
            this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
                category: TrackedEventCategory.EXPLORE,
                name: this.SHOW_ALL
            });

        this.router.navigate([`stop/${stopId}`], { relativeTo: this.route, queryParams: { centering: true } });
    }

    openLocation(location: FavoriteLocation) {
        this.router.navigate(['location'], {
            relativeTo: this.route,
            queryParams: {
                latitude: location.latitude,
                longitude: location.longitude,
                name: location.name,
                pointOfInterest: location.pointOfInterest,
                centering: true
            }
        });
    }

    openTripDetailsView($event: TripClickEvent) {
        this.router.navigate([`trip`], { relativeTo: this.route, queryParams: $event });
    }

    openPatternsView(routeId: string) {
        this.router.navigate([`route/${routeId}/patterns`], { relativeTo: this.route });
    }

    handleTabSelect(tab: string) {
        this.selectedTab = tab;
        if (tab !== this.TABS.favorites) {
            // reset on leave
            this.showNumberOfFavorites = this.FAVORITES_INCREMENT;
            this.favoriteStopsShown = [...this.favoriteStopsSaved.slice(0, this.showNumberOfFavorites)];
        }
        this.router.navigate([], { queryParams: { selectedTab: tab } });
    }

    showNearbyTab() {
        return !this.hasLocationError && this.env.EXPLORE?.SHOW_FAVORITE_STOPS && this.deviceStatus;
    }

    showRoutesTab() {
        return this.env.EXPLORE?.SHOW_ROUTES && this.deviceStatus;
    }

    showFavoritesTab() {
        return (
            ((this.favoriteLocations?.length || this.favoriteStopsSaved?.length) &&
                this.env.EXPLORE?.SHOW_FAVORITE_STOPS) ||
            this.favoriteSchedules?.length ||
            (this.env.EXPLORE?.SHOW_FAVORITE_SCHEDULE && !this.deviceStatus)
        );
    }

    getTabsCount() {
        return [this.showNearbyTab(), this.showRoutesTab(), this.showFavoritesTab()].filter(Boolean).length;
    }

    get isBoundLocation() {
        const coordinates = this.coordinates || this.openTripPlannerFacade.сurrentCoordinates;
        if (!Capacitor.isNativePlatform() || !coordinates || !this.env.MAP.MAX_BOUNDS) {
            return 'NO_NEARBY_STOPS_FOUND';
        }

        return !this.mapApi.checkIsContainBound(coordinates) ? 'LOCATION_OUTSIDE' : 'NO_NEARBY_STOPS_FOUND';
    }

    getTabIndex(position) {
        return [this.showNearbyTab(), this.showRoutesTab(), this.showFavoritesTab()].splice(0, position).filter(Boolean)
            .length;
    }

    async handleEditFavorites() {
        const modal = await this.modalCtrl.create({
            component: FavoritesListModalComponent,
            cssClass: 'favorites-list-modal',
            componentProps: {
                switchToNearbyTab: () => this.handleTabSelect(this.TABS.nearby)
            }
        });

        const content = document.querySelector('ion-content');
        await modal.present().then(() => {
            if (content) {
                content.setAttribute('inert', '');
            }
        });
        await modal.onDidDismiss().then(() => {
            if (content) {
                content.removeAttribute('inert');
            }
        });
    }

    private updateMapStopsState(): void {
        this.openTripPlannerFacade.setExploreMapStops(this.getNearbyStopsPatch());
    }

    private getFavoriteStopsToLoad(): string[] {
        return this.favoriteStopsSaved
            .slice(0, this.showNumberOfFavorites)
            .filter((f) => !this.stopIdToDepartures.has(f.id))
            .map((f) => f.id);
    }

    //FIXME: this is spammed too much, make move to state and handle async
    @Debounce(300)
    private updateStopTimesOfNewVisibleStops(): void {
        const stopIds = [...this.getNotLoadedStopIds(), ...this.getFavoriteStopsToLoad()];
        if (!stopIds.length) {
            return;
        }

        const fromTime = this.getFromTimeForDepartures();

        stopIds.forEach((stopId) => {
            this.departuresLoadingMap[stopId] = true;
            this.otpService
                .getUpcomingDepartures({
                    stopId,
                    timeRangeSeconds: this.DEPARTURES_SEARCH_RANGE_SECONDS,
                    numberOfDepartures: this.env.EXPLORE?.DEPARTURES_REQUEST_MAX_NO_PATCHING
                        ? this.env.EXPLORE?.MAX_DEPARTURES_WITH_SELECTED_ROUTE_FILTER
                        : 3,
                    fromTime
                })
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((departures) => {
                    this.stopIdToDepartures.set(stopId, departures);
                    this.favoriteStopsShown = [...this.favoriteStopsSaved.slice(0, this.showNumberOfFavorites)];
                })
                .add(() => {
                    this.departuresLoadingMap[stopId] = false;
                });
        });
    }

    public getFromTimeForDepartures(): number {
        const now = Temporal.Now.plainDateTimeISO(this.timezone).toString();
        return Temporal.PlainDateTime.from(now).toZonedDateTime(this.timezone).epochSeconds;
    }

    public refreshStopTimes(_stopIds?: string[]): void {
        const stopIds = _stopIds || Array.from(this.stopIdToDepartures.keys());

        // Clear existing departures and set loading state to show skeleton loaders
        stopIds.forEach((stopId) => {
            this.stopIdToDepartures.set(stopId, []);
            this.departuresLoadingMap[stopId] = true;
        });
        // Trigger change detection to show loading state immediately
        this.cdr.detectChanges();
        const fromTime = this.getFromTimeForDepartures();

        stopIds.forEach((stopId) => {
            this.otpService
                .getUpcomingDepartures({
                    stopId,
                    timeRangeSeconds: this.DEPARTURES_SEARCH_RANGE_SECONDS,
                    fromTime,
                    numberOfDepartures: this.env.EXPLORE?.DEPARTURES_REQUEST_MAX_NO_PATCHING
                        ? this.env.EXPLORE?.MAX_DEPARTURES_WITH_SELECTED_ROUTE_FILTER
                        : 3
                })
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((departures) => {
                    this.stopIdToDepartures.set(stopId, departures);
                    this.departuresLoadingMap[stopId] = false;
                    this.cdr.detectChanges();
                });
        });
    }

    public handleRefreshClick(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        const now = Temporal.Now.instant();
        this.lastRefreshTimeSubject$.next(now);
        this.currentTimeSubject$.next(now);
        this.refreshStopTimes();
    }

    private getNotLoadedStopIds(): string[] {
        return this.getNearbyStopsPatch()
            .map((s) => s.id)
            .filter((stopId) => !this.stopIdToDepartures.has(stopId));
    }

    private syncFavorites() {
        // we need timeout for sync local favorites data with backend data
        setTimeout(() => {
            const favoriteStopIds = this.favoriteStopsSaved.map((favorite) => favorite.id || favorite.stopId);
            this.favoritesFacade.addFavoriteStopFromLocalstorage(favoriteStopIds);
        }, 1000);
    }

    handleSearchItemSelect(selectedSearchItem: StopRouteResult | AddressResult) {
        switch (selectedSearchItem.type) {
            case TransitNetworkPartType.Stop:
                this.openStopView(selectedSearchItem.id, true);
                break;
            case TransitNetworkPartType.Route:
                this.openPatternsView(selectedSearchItem.id);
                break;
            case TransitNetworkPartType.Location:
                this.openLocation({
                    name: selectedSearchItem.name || (selectedSearchItem as AddressResult).address,
                    latitude: (selectedSearchItem as AddressResult).lat,
                    longitude: (selectedSearchItem as AddressResult).lon
                });
                break;
            case TransitNetworkPartType.POI:
                this.openLocation({
                    name: selectedSearchItem.name,
                    latitude: (selectedSearchItem as AddressResult).lat,
                    longitude: (selectedSearchItem as AddressResult).lon,
                    pointOfInterest: true
                });
                break;
        }
    }

    private calculateDistanceToPreviousPoint(coordinates) {
        return getDistance(
            {
                latitude: this.lastCheckedLocation.lat,
                longitude: this.lastCheckedLocation.lng
            },
            {
                latitude: coordinates.lat,
                longitude: coordinates.lng
            }
        );
    }

    hasFavorites(): boolean {
        return (
            (this.env.EXPLORE?.SHOW_FAVORITE_STOPS &&
                (!!this.favoriteLocations?.length || !!this.favoriteStopsSaved?.length)) ||
            (!!this.favoriteSchedules?.length && this.env.EXPLORE?.SHOW_FAVORITE_SCHEDULE) ||
            (!this.deviceStatus && this.env.EXPLORE?.SHOW_FAVORITE_SCHEDULE)
        );
    }

    ngOnDestroy() {
        this.lastRefreshTimeSubject$.complete();
        this.currentTimeSubject$.complete();
        this.smallScreenSheetModalContainerService.setHeaderTemplate(null);
        super.ngOnDestroy();
    }
}
